/**
 * `AircraftKey` → baked model id (`data/baked/models/<id>.json`, the id a
 * `models`-category entry carries in `data/baked/manifest.json`). A plain
 * table, not a formula: every `.TBL` in the game's asset set happens to share
 * its basename with the aircraft's engine key today, but that is a fact about
 * the current bake, not a guarantee this file should compute its way around —
 * a table can be edited to point one key at a differently-named asset, or to
 * `null`, without the runtime function changing shape.
 *
 * The table is not verified against the manifest here — that would make this
 * module read `data/` at import time, which the flight core never does. The
 * check runs at *test* time instead (`flight-baked-models.test.ts`, read-only
 * against the real manifest), which is what actually keeps this honest: it
 * fails if an entry here claims an id that was never really baked.
 *
 * `null` is a first-class, tested outcome, not an error case to work around —
 * several of the fleet's AI-only two-seaters were expected not to have baked
 * geometry when this table was written. As of the current bake every one of
 * the 33 keys resolves (see the test for the up-to-date list), but a caller
 * must still handle `null` rather than assume it.
 */

import type { AircraftKey } from "./aircraft-table.ts";

const BAKED_MODEL_IDS: Readonly<Record<AircraftKey, string | null>> = {
  MORANE: "models/MORANE",
  FOKE3: "models/FOKE3",
  AIRCO: "models/AIRCO",
  NIEU11: "models/NIEU11",
  NIEUPORT: "models/NIEUPORT",
  SOPPUP: "models/SOPPUP",
  SPAD_7: "models/SPAD_7",
  ALBA_D2: "models/ALBA_D2",
  SOPTRI: "models/SOPTRI",
  ALBA_D3: "models/ALBA_D3",
  HALBD2: "models/HALBD2",
  SE5: "models/SE5",
  ALBA_D5: "models/ALBA_D5",
  CAMEL: "models/CAMEL",
  PFALZ: "models/PFALZ",
  FOKDR1: "models/FOKDR1",
  SPAD_13: "models/SPAD_13",
  NIEU28: "models/NIEU28",
  FOKD7: "models/FOKD7",
  SIEMENS: "models/SIEMENS",
  SNIPE: "models/SNIPE",
  FOKD8: "models/FOKD8",
  BE2: "models/BE2",
  AVIATIK: "models/AVIATIK",
  FE2: "models/FE2",
  ROLAND: "models/ROLAND",
  RE8: "models/RE8",
  RUMPLER: "models/RUMPLER",
  BRISTOL: "models/BRISTOL",
  DH4: "models/DH4",
  JUNKER: "models/JUNKER",
  GOTHA: "models/GOTHA",
  HANDLEY: "models/HANDLEY",
};

/** The baked model id for `key`'s airframe, or `null` if it has no baked geometry. */
export function bakedModelIdOf(key: AircraftKey): string | null {
  return BAKED_MODEL_IDS[key];
}
